import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BASE_URL } from '../../services/configUrls';
import { Icon } from '@iconify/react';

// Group menus by parent for display
const groupMenus = (menus) => {
  const groups = {};
  const topLevel = [];

  menus.forEach(menu => {
    if (!menu.parent_title) {
      topLevel.push(menu);
    } else {
      if (!groups[menu.parent_title]) groups[menu.parent_title] = [];
      groups[menu.parent_title].push(menu);
    }
  });

  return { topLevel, groups };
};

const ROLES = [
  { key: 'dspoc',    label: 'D-SPOC',   color: '#7c3aed', icon: 'mdi:account-supervisor' },
  { key: 'leaders',  label: 'Leaders',  color: '#0ea5e9', icon: 'mdi:account-star'        },
  { key: 'educator', label: 'Educator', color: '#10b981', icon: 'ph:chalkboard-teacher-bold' },
  { key: 'tpo',      label: 'TPO',      color: '#f59e0b', icon: 'mdi:briefcase-account'   },
];

const SpocRoleAccess = () => {
  const [selectedRole, setSelectedRole]   = useState('dspoc');
  const [checkedPaths, setCheckedPaths]   = useState([]);
  const [defaultPaths, setDefaultPaths]   = useState([]);
  const [allMenus, setAllMenus]           = useState([]);
  const [loading, setLoading]             = useState(false);
  const [menuLoading, setMenuLoading]     = useState(false);
  const [saving, setSaving]               = useState(false);
  const [toast, setToast]                 = useState(null);

  const token = localStorage.getItem('accessToken');

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Fetch all menus from DB once on mount
  useEffect(() => {
    const fetchAllMenus = async () => {
      setMenuLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/spoc-access/menus`);
        setAllMenus(res.data.menus || []);
      } catch (err) {
        console.error('Error fetching menus:', err);
        showToast('Could not load menu list from server.', 'error');
      } finally {
        setMenuLoading(false);
      }
    };
    fetchAllMenus();
  }, []);

  // Fetch enabled paths for selected role
  const fetchRoleAccess = useCallback(async (role) => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/spoc-access?role_name=${role}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCheckedPaths(res.data.allowed_paths || []);
      setDefaultPaths(res.data.default_paths || []);
    } catch (err) {
      console.error('Error fetching role access:', err);
      setCheckedPaths([]);
      setDefaultPaths([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchRoleAccess(selectedRole);
  }, [selectedRole, fetchRoleAccess]);

  const handleToggle = (path) => {
    if (path === '/dashboard' || defaultPaths.includes(path)) return; // always enabled
    setCheckedPaths(prev =>
      prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
    );
  };

  const handleSelectAll = () => {
    const all = allMenus.map(m => m.path);
    setCheckedPaths([...new Set([...all, '/dashboard'])]);
  };

  const handleClearAll = () => {
    setCheckedPaths(['/dashboard', ...defaultPaths]);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${BASE_URL}/spoc-access/update`,
        { role_name: selectedRole, allowed_paths: [...new Set([...checkedPaths, ...defaultPaths])] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast(`Access updated for ${ROLES.find(r => r.key === selectedRole)?.label}!`);
    } catch (err) {
      showToast('Failed to save. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const activeRole = ROLES.find(r => r.key === selectedRole);
  const { topLevel, groups } = groupMenus(allMenus);

  // Default menus (locked ON)
  const defaultMenus = allMenus.filter(m => defaultPaths.includes(m.path) && m.path !== '/dashboard');

  // Menus that are top-level (no parent) AND not dashboard AND not default
  const mainMenus = topLevel.filter(m => m.path !== '/dashboard' && !defaultPaths.includes(m.path));
  
  // Filter groups to exclude default menus
  const filteredGroups = {};
  Object.keys(groups).forEach(key => {
    const nonDefaultChildren = groups[key].filter(m => !defaultPaths.includes(m.path));
    if (nonDefaultChildren.length > 0) {
      filteredGroups[key] = nonDefaultChildren;
    }
  });
  const parentKeys = Object.keys(filteredGroups);

  const enabledCount = [...new Set([...checkedPaths, ...defaultPaths])].filter(p => p !== '/dashboard').length;

  return (
    <div style={{ minHeight: '100vh', padding: '24px', fontFamily: "'Inter', 'Segoe UI', sans-serif", backgroundColor: '#fff' }}>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'success' ? '#10b981' : '#ef4444',
          color: '#fff', borderRadius: 12, padding: '14px 22px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600,
          animation: 'slideIn 0.3s ease'
        }}>
          <Icon icon={toast.type === 'success' ? 'mdi:check-circle' : 'mdi:alert-circle'} height={20} />
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 6 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14,
            background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 20px rgba(99,102,241,0.3)'
          }}>
            <Icon icon="mdi:shield-account" height={26} color="#fff" />
          </div>
          <div>
            <h1 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1e1b4b', letterSpacing: '-0.5px' }}>
              Role Access Management
            </h1>
            <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
              Grant or revoke sidebar &amp; route access for each role in your institute
            </p>
          </div>
        </div>
      </div>

      {/* Role Tabs */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        {ROLES.map(role => (
          <button key={role.key} onClick={() => setSelectedRole(role.key)} style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', borderRadius: 10, fontWeight: 700,
            fontSize: 13, border: 'none', cursor: 'pointer', transition: 'all 0.2s',
            background: selectedRole === role.key ? role.color : '#fff',
            color: selectedRole === role.key ? '#fff' : '#374151',
            boxShadow: selectedRole === role.key
              ? `0 4px 18px ${role.color}60`
              : '0 2px 8px rgba(0,0,0,0.07)',
            transform: selectedRole === role.key ? 'translateY(-1px)' : 'none'
          }}>
            <Icon icon={role.icon} height={18} />
            {role.label}
          </button>
        ))}
      </div>

      {/* Main Panel */}
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 4px 28px rgba(0,0,0,0.07)', overflow: 'hidden' }}>

        {/* Card Header */}
        <div style={{
          padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: `linear-gradient(135deg, ${activeRole?.color}12, ${activeRole?.color}04)`,
          borderBottom: `2px solid ${activeRole?.color}20`
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Icon icon={activeRole?.icon} height={20} color={activeRole?.color} />
            <span style={{ fontWeight: 700, fontSize: 14, color: '#1e1b4b' }}>
              {activeRole?.label} Access
            </span>
            <span style={{
              background: `${activeRole?.color}18`, color: activeRole?.color,
              borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 700
            }}>
              {enabledCount} of {allMenus.length} enabled
            </span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleSelectAll} style={{
              padding: '4px 12px', borderRadius: 8, fontWeight: 600, fontSize: 11, cursor: 'pointer',
              border: `1.5px solid ${activeRole?.color}50`, background: 'transparent', color: activeRole?.color
            }}>Select All</button>
            <button onClick={handleClearAll} style={{
              padding: '4px 12px', borderRadius: 8, fontWeight: 600, fontSize: 11, cursor: 'pointer',
              border: '1.5px solid #e5e7eb', background: 'transparent', color: '#6b7280'
            }}>Clear All</button>
          </div>
        </div>

        <div style={{ padding: 20 }}>
          {menuLoading ? (
            <div style={{ textAlign: 'center', padding: 56, color: '#9ca3af' }}>
              <Icon icon="mdi:loading" height={36} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 12, fontSize: 14 }}>Loading menus from database...</p>
            </div>
          ) : loading ? (
            <div style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>
              <Icon icon="mdi:loading" height={32} style={{ animation: 'spin 1s linear infinite' }} />
              <p style={{ marginTop: 10, fontSize: 13 }}>Loading role configuration...</p>
            </div>
          ) : (
            <>
              {/* Always on menus */}
              <div style={{ marginBottom: 20 }}>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>
                  Always Enabled
                </p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: 10,
                    background: '#f0fdf4', border: '1.5px solid #bbf7d0'
                  }}>
                    <div style={{ width: 18, height: 18, borderRadius: 4, background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon icon="mdi:check" height={12} color="#fff" />
                    </div>
                    <Icon icon="mingcute:classify-2-fill" height={14} color="#065f46" />
                    <span style={{ fontWeight: 600, fontSize: 12, color: '#065f46' }}>Dashboard</span>
                    <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 4 }}>/dashboard</span>
                  </div>
                  
                  {defaultMenus.map(menu => (
                    <div key={menu.path} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 10,
                      background: '#f0fdf4', border: '1.5px solid #bbf7d0'
                    }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, background: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon icon="mdi:lock" height={11} color="#fff" />
                      </div>
                      {menu.icon_name && <Icon icon={menu.icon_name} height={14} color="#065f46" />}
                      <span style={{ fontWeight: 600, fontSize: 12, color: '#065f46' }}>{menu.title}</span>
                      <span style={{ fontSize: 10, color: '#6b7280', marginLeft: 4 }}>{menu.path}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top-level menus */}
              {mainMenus.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>
                    Main Menus ({mainMenus.length})
                  </p>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                    {mainMenus.map(menu => {
                      const isDefault = defaultPaths.includes(menu.path);
                      const isChecked = checkedPaths.includes(menu.path) || isDefault;
                      return (
                        <div key={`${menu.title}-${menu.path}`} onClick={() => handleToggle(menu.path)} style={{
                          display: 'flex', alignItems: 'center', gap: 8,
                          padding: '8px 12px', borderRadius: 10, cursor: isDefault ? 'not-allowed' : 'pointer',
                          background: isChecked ? (isDefault ? '#f3f4f6' : `${activeRole?.color}08`) : '#f9fafb',
                          border: `1.5px solid ${isChecked ? (isDefault ? '#d1d5db' : activeRole?.color + '45') : '#e5e7eb'}`,
                          transition: 'all 0.15s',
                          opacity: isDefault ? 0.7 : 1
                        }}>
                          <div style={{
                            width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                            background: isChecked ? (isDefault ? '#9ca3af' : activeRole?.color) : '#fff',
                            border: `2px solid ${isChecked ? (isDefault ? '#9ca3af' : activeRole?.color) : '#d1d5db'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            transition: 'all 0.15s'
                          }}>
                            {isChecked && (isDefault ? <Icon icon="mdi:lock" height={11} color="#fff" /> : <Icon icon="mdi:check" height={11} color="#fff" />)}
                          </div>
                          {menu.icon_name && <Icon icon={menu.icon_name} height={14} color={isChecked ? (isDefault ? '#6b7280' : activeRole?.color) : '#6b7280'} />}
                          <div style={{ overflow: 'hidden' }}>
                            <div style={{ fontWeight: 600, fontSize: 12, color: isChecked ? '#1e1b4b' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {menu.title}
                            </div>
                            <div style={{ fontSize: 9, color: '#9ca3af' }}>{menu.path}</div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Sub-menus grouped by parent */}
              {parentKeys.map(parentTitle => {
                const children = filteredGroups[parentTitle];
                return (
                  <div key={parentTitle} style={{ marginBottom: 20 }}>
                    <p style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.08em', textTransform: 'uppercase', margin: '0 0 8px' }}>
                      {parentTitle} — Sub menus ({children.length})
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                      {children.map(menu => {
                        const isDefault = defaultPaths.includes(menu.path);
                        const isChecked = checkedPaths.includes(menu.path) || isDefault;
                        return (
                          <div key={`${menu.title}-${menu.path}`} onClick={() => handleToggle(menu.path)} style={{
                            display: 'flex', alignItems: 'center', gap: 8,
                            padding: '8px 12px', borderRadius: 10, cursor: isDefault ? 'not-allowed' : 'pointer',
                            background: isChecked ? (isDefault ? '#f3f4f6' : `${activeRole?.color}08`) : '#fafafa',
                            border: `1.5px solid ${isChecked ? (isDefault ? '#d1d5db' : activeRole?.color + '45') : '#eeeeee'}`,
                            transition: 'all 0.15s',
                            marginLeft: 12, borderLeft: `3px solid ${isChecked ? (isDefault ? '#9ca3af' : activeRole?.color) : '#e5e7eb'}`,
                            opacity: isDefault ? 0.7 : 1
                          }}>
                            <div style={{
                              width: 16, height: 16, borderRadius: 4, flexShrink: 0,
                              background: isChecked ? (isDefault ? '#9ca3af' : activeRole?.color) : '#fff',
                              border: `2px solid ${isChecked ? (isDefault ? '#9ca3af' : activeRole?.color) : '#d1d5db'}`,
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              {isChecked && (isDefault ? <Icon icon="mdi:lock" height={10} color="#fff" /> : <Icon icon="mdi:check" height={10} color="#fff" />)}
                            </div>
                            {menu.icon_name && <Icon icon={menu.icon_name} height={12} color={isChecked ? (isDefault ? '#6b7280' : activeRole?.color) : '#9ca3af'} />}
                            <div style={{ overflow: 'hidden' }}>
                              <div style={{ fontWeight: 600, fontSize: 12, color: isChecked ? '#1e1b4b' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{menu.title}</div>
                              <div style={{ fontSize: 9, color: '#9ca3af' }}>{menu.path}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        <div style={{ borderTop: '1px solid #f3f4f6', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          
          <button onClick={handleSave} disabled={saving} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 20px', borderRadius: 10,
            background: saving ? '#a5b4fc' : 'linear-gradient(135deg, #6366f1, #8b5cf6)',
            color: '#fff', border: 'none', fontWeight: 700, fontSize: 13,
            cursor: saving ? 'not-allowed' : 'pointer',
            boxShadow: '0 4px 16px rgba(99,102,241,0.35)', transition: 'all 0.2s'
          }}>
            {saving
              ? <><Icon icon="mdi:loading" height={16} style={{ animation: 'spin 1s linear infinite' }} /> Saving...</>
              : <><Icon icon="mdi:content-save" height={16} /> Save Changes</>
            }
          </button>
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes slideIn { from { transform: translateX(100px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
      `}</style>
    </div>
  );
};

export default SpocRoleAccess;